import {useCallback, useState} from 'react'

interface UseDismissibleBannerOptions {
  onDismiss?: () => void
}

/**
 * Tracks whether a banner has been dismissed, auto-resetting when `active`
 * transitions from false → true (i.e. a new "cycle" begins).
 *
 * Pass `onDismiss` to fire a side effect (e.g. persist dismissal to the server)
 * when the user dismisses the banner.
 */
export function useDismissibleBanner(active: boolean, options?: UseDismissibleBannerOptions) {
  const [dismissed, setDismissed] = useState(false)
  const [prevActive, setPrevActive] = useState(active)

  if (active !== prevActive) {
    setPrevActive(active)
    // Reset dismissal when a new active cycle starts
    if (active && dismissed) {
      setDismissed(false)
    }
  }

  const onDismiss = options?.onDismiss
  const dismiss = useCallback(() => {
    setDismissed(true)
    onDismiss?.()
  }, [onDismiss])

  const visible = active && !dismissed

  return {visible, dismiss}
}
